import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, User } from "lucide-react";
import { apiUrl } from "@/api/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface CarouselItem {
  doc_id: string;
  thumbnail_url: string;
  optimized_url?: string;
  image_caption: string;
  rating?: number;
  ai_rating?: string;
  status?: string;
  origin?: string;
}

export interface CarouselProps {
  items: CarouselItem[];
  onSelect: (item: CarouselItem) => void;
  displayMode?: "modal" | "inline";
}

export const Carousel: React.FC<CarouselProps> = ({
  items,
  onSelect,
  displayMode = "modal",
}) => {
  const [startIndex, setStartIndex] = useState(0);
  const [previewIndex, setPreviewIndex] = useState<number>(-1);
  const itemsToShow = 6;

  const showPrev = (e: React.MouseEvent) => {
    e.preventDefault();
    setStartIndex(Math.max(0, startIndex - 1));
  };

  const showNext = (e: React.MouseEvent) => {
    e.preventDefault();
    setStartIndex(Math.min(items.length - itemsToShow, startIndex + 1));
  };

  const handleItemClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (displayMode === "modal") {
      setPreviewIndex(index);
    }
    onSelect(items[index]);
    return false;
  };

  const showModalPrev = (e: React.MouseEvent) => {
    e.preventDefault();
    if (previewIndex > 0) {
      setPreviewIndex(previewIndex - 1);
      onSelect(items[previewIndex - 1]);
    }
  };

  const showModalNext = (e: React.MouseEvent) => {
    e.preventDefault();
    if (previewIndex < items.length - 1) {
      setPreviewIndex(previewIndex + 1);
      onSelect(items[previewIndex + 1]);
    }
  };

  if (!items.length) return null;

  const previewItem = previewIndex >= 0 ? items[previewIndex] : null;

  return (
    <>
      <Card className="bg-black/20 border-white/10 backdrop-blur-sm hover:border-primary/20 transition-all duration-200">
        <CardContent className="px-6 py-4 overflow-hidden">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-primary">
              AI Generated Images
            </h3>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={showPrev}
                disabled={startIndex === 0}
                className="hover:bg-[#DFFE00]/10 text-primary disabled:text-white/20"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={showNext}
                disabled={startIndex >= items.length - itemsToShow}
                className="hover:bg-[#DFFE00]/10 text-primary disabled:text-white/20"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-4 flex-row overflow-hidden">
            {items
              .slice(startIndex, startIndex + itemsToShow)
              .map((item, idx) => {
                const globalIndex = startIndex + idx;

                return (
                  <Button
                    key={item.doc_id}
                    variant="ghost"
                    className="relative group p-0 h-auto hover:bg-transparent"
                    onClick={(e) => handleItemClick(e, globalIndex)}
                  >
                    <div className="flex-shrink-0 w-[232px] aspect-[3/2] overflow-hidden rounded-lg border border-white/10 hover:border-primary/50 transition-all duration-200">
                      <img
                        src={`${apiUrl}/get-file?url=${encodeURIComponent(
                          item.thumbnail_url,
                        )}`}
                        alt={item.image_caption}
                        className={`w-full h-full ${item.origin === "uploaded" ? "object-contain border-yellow-500 border-solid" : "object-cover"} transition-transform duration-200 group-hover:scale-105`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleItemClick(e, globalIndex);
                        }}
                      />
                      {item.thumbnail_url?.includes("uploaded") && (
                        <User className="absolute top-2 right-2 w-4 h-4 text-black pointer-events-none rounded-full ring-2 ring-white bg-white/70 backdrop-blur-sm" />
                      )}
                      <div className="rounded-lg absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-200">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-sm text-white/90 truncate">
                            {item.image_caption}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Button>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {displayMode === "modal" && (
        <Dialog
          open={previewIndex >= 0}
          onOpenChange={(open) => {
            if (!open) setPreviewIndex(-1);
          }}
        >
          <DialogContent className="max-w-4xl bg-black/90 border-white/10 backdrop-blur-xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-primary">
                {previewItem?.image_caption || ""}
              </DialogTitle>
            </DialogHeader>
            {previewItem && (
              <div className="relative flex flex-col items-center">
                <div className="relative w-full aspect-[3/2] rounded-lg overflow-hidden border border-white/10">
                  <img
                    src={`${apiUrl}/get-file?url=${encodeURIComponent(
                      previewItem.optimized_url || previewItem.thumbnail_url,
                    )}`}
                    alt={previewItem.image_caption}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex items-center justify-between w-full mt-6">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={showModalPrev}
                    disabled={previewIndex <= 0}
                    className="border-white/10 text-primary hover:bg-[#DFFE00]/10 hover:text-primary disabled:text-white/20"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={showModalNext}
                    disabled={previewIndex >= items.length - 1}
                    className="border-white/10 text-primary hover:bg-[#DFFE00]/10 hover:text-primary disabled:text-white/20"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
